/* The right-hand drawer, and the modal.
 *
 * Both are rendered through a portal into `document.body`. The reason is not
 * tidiness: the console's main area scrolls, and a drawer positioned `fixed`
 * inside a scrolling, transformed ancestor is positioned relative to that
 * ancestor instead of the viewport — which shows up as a drawer that stops at
 * the bottom of the content rather than the bottom of the screen.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import css from './ui.module.css';
import { Icon } from './primitives';

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
}

export function Drawer({
  open,
  onClose,
  label,
  wide,
  full,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  wide?: boolean;
  /** The whole viewport, for a view that is the content (the live map). */
  full?: boolean;
  children: ReactNode;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <button type="button" className={css.scrim} onClick={onClose} aria-label={`Close ${label}`} />
      <aside
        className={full ? `${css.drawer} ${css.drawerFull}` : wide ? `${css.drawer} ${css.drawerWide}` : css.drawer}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </aside>
    </>,
    document.body,
  );
}

export function DrawerHead({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className={css.drawerHead}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div className={css.grow}>{children}</div>
        <button type="button" className={css.iconBtn} onClick={onClose} aria-label="Close">
          <Icon name="close" size={15} />
        </button>
      </div>
    </div>
  );
}

export function DrawerBody({ children }: { children: ReactNode }) {
  return <div className={css.drawerBody}>{children}</div>;
}

export function DrawerFoot({ children }: { children: ReactNode }) {
  return <div className={css.drawerFoot}>{children}</div>;
}

export function Modal({
  open,
  onClose,
  label,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div
      className={css.modalScrim}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={css.modal}>{children}</div>
    </div>,
    document.body,
  );
}
